import spaceshipsJson from "./generated/spaceships.json";
import { globalRepo, K } from "./globalRepo.js";
import type { GridPosition, ModuleGridLane, ModuleGridState } from "../models/ModuleGrid.js";

export type SpaceshipUnlockConditionType = "default" | "chapter" | "world";

interface SpaceshipRow {
  readonly id: string;
  readonly nameKey: string;
  readonly descKey: string;
  readonly launcherNameKey: string;
  readonly launcherDescKey: string;
  readonly launcherIconKey: string;
  readonly userDescription: string;
  readonly imagePrompt: string;
  readonly gridWidth: number;
  readonly gridHeight: number;
  readonly gridMap: string;
  readonly wiringLanes: string;
  readonly projectileMuzzles: string;
  readonly projectileAssetKey: string;
  readonly shipScale: number;
  readonly enemyScale: number;
  readonly unlockConditionType: string;
  readonly unlockConditionValue: number;
  readonly unlockConditionKey: string;
  readonly smallAssetKey: string;
  readonly largeAssetKey: string;
}

export interface SpaceshipGridSize {
  readonly width: number;
  readonly height: number;
}

export interface SpaceshipUnlockCondition {
  readonly type: SpaceshipUnlockConditionType;
  readonly value: number;
  readonly messageKey: string;
}

export interface SpaceshipProjectileMuzzle {
  readonly offsetX: number;
  readonly offsetY: number;
}

export interface SpaceshipDefinition {
  readonly id: string;
  readonly nameKey: string;
  readonly descKey: string;
  readonly launcherNameKey: string;
  readonly launcherDescKey: string;
  readonly launcherIconKey: string;
  readonly userDescription: string;
  readonly imagePrompt: string;
  readonly gridSize: SpaceshipGridSize;
  readonly gridMap: string;
  readonly wiringLanes: readonly ModuleGridLane[];
  readonly projectileMuzzles: readonly SpaceshipProjectileMuzzle[];
  readonly projectileAssetKey: string;
  readonly shipScale: number;
  readonly enemyScale: number;
  readonly unlockCondition: SpaceshipUnlockCondition;
  readonly smallAssetKey: string;
  readonly largeAssetKey: string;
}

function requireNonEmpty(value: string, field: string, id: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`spaceships.tsv: ${id}.${field} is empty`);
  }
  return value;
}

function requirePositiveInteger(value: number, field: string, id: string): number {
  if (!Number.isInteger(value) || value < 1) {
    throw new Error(`spaceships.tsv: ${id}.${field} must be a positive integer`);
  }
  return value;
}

function requirePositiveFiniteNumber(value: number, field: string, id: string): number {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`spaceships.tsv: ${id}.${field} must be a positive finite number`);
  }
  return value;
}

function toUnlockCondition(row: SpaceshipRow, id: string): SpaceshipUnlockCondition {
  const type = requireNonEmpty(row.unlockConditionType, "unlockConditionType", id);
  if (type !== "default" && type !== "chapter" && type !== "world") {
    throw new Error(`spaceships.tsv: ${id}.unlockConditionType must be default, chapter, or world`);
  }
  if (!Number.isInteger(row.unlockConditionValue) || row.unlockConditionValue < 0) {
    throw new Error(`spaceships.tsv: ${id}.unlockConditionValue must be a non-negative integer`);
  }
  if (type === "chapter" && row.unlockConditionValue < 1) {
    throw new Error(`spaceships.tsv: ${id}.unlockConditionValue must be >= 1 for chapter unlock`);
  }
  if (type === "world" && row.unlockConditionValue < 1) {
    throw new Error(`spaceships.tsv: ${id}.unlockConditionValue must be >= 1 for world unlock`);
  }
  return {
    type,
    value: row.unlockConditionValue,
    messageKey: requireNonEmpty(row.unlockConditionKey, "unlockConditionKey", id),
  };
}

function parsePosition(raw: string, field: string, id: string): GridPosition {
  const [xRaw, yRaw] = raw.split(",");
  const x = Number(xRaw);
  const y = Number(yRaw);
  if (!Number.isInteger(x) || !Number.isInteger(y)) {
    throw new Error(`spaceships.tsv: ${id}.${field} '${raw}' must be x,y`);
  }
  return { x, y };
}

function parseWiringLanes(raw: string, id: string): readonly ModuleGridLane[] {
  const lanes = requireNonEmpty(raw, "wiringLanes", id)
    .split("|")
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .map((part, i) => {
      const [startRaw, fireRaw] = part.split(">");
      if (!startRaw || !fireRaw) throw new Error(`spaceships.tsv: ${id}.wiringLanes[${i}] must be start>fire`);
      return {
        start: parsePosition(startRaw, `wiringLanes[${i}].start`, id),
        fire: parsePosition(fireRaw, `wiringLanes[${i}].fire`, id),
      };
    });
  if (lanes.length === 0) throw new Error(`spaceships.tsv: ${id}.wiringLanes must contain at least one lane`);
  return lanes;
}

function parseProjectileMuzzles(raw: string, id: string): readonly SpaceshipProjectileMuzzle[] {
  const muzzles = requireNonEmpty(raw, "projectileMuzzles", id)
    .split("|")
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .map((part, i) => {
      const pos = parsePosition(part, `projectileMuzzles[${i}]`, id);
      return { offsetX: pos.x, offsetY: pos.y };
    });
  if (muzzles.length === 0) throw new Error(`spaceships.tsv: ${id}.projectileMuzzles must contain at least one muzzle`);
  return muzzles;
}

function validateGridMap(raw: string, width: number, height: number, id: string): void {
  const rows = requireNonEmpty(raw, "gridMap", id).split("/");
  if (rows.length !== height) {
    throw new Error(`spaceships.tsv: ${id}.gridMap height ${rows.length} does not match gridHeight ${height}`);
  }
  for (let y = 0; y < rows.length; y++) {
    const row = rows[y]!;
    if (row.length !== width) {
      throw new Error(`spaceships.tsv: ${id}.gridMap row ${y} width ${row.length} does not match gridWidth ${width}`);
    }
    for (const ch of row) {
      if (ch !== "." && ch !== "#" && ch !== "L") {
        throw new Error(`spaceships.tsv: ${id}.gridMap contains invalid char '${ch}'`);
      }
    }
  }
}

function toSpaceshipDefinition(row: SpaceshipRow): SpaceshipDefinition {
  const id = requireNonEmpty(row.id, "id", row.id);
  const width = requirePositiveInteger(row.gridWidth, "gridWidth", id);
  const height = requirePositiveInteger(row.gridHeight, "gridHeight", id);
  validateGridMap(row.gridMap, width, height, id);
  const wiringLanes = parseWiringLanes(row.wiringLanes, id);
  const projectileMuzzles = parseProjectileMuzzles(row.projectileMuzzles, id);
  if (projectileMuzzles.length < wiringLanes.length) {
    throw new Error(`spaceships.tsv: ${id}.projectileMuzzles must contain at least one muzzle per wiring lane`);
  }
  return {
    id,
    nameKey: requireNonEmpty(row.nameKey, "nameKey", id),
    descKey: requireNonEmpty(row.descKey, "descKey", id),
    launcherNameKey: requireNonEmpty(row.launcherNameKey, "launcherNameKey", id),
    launcherDescKey: requireNonEmpty(row.launcherDescKey, "launcherDescKey", id),
    launcherIconKey: requireNonEmpty(row.launcherIconKey, "launcherIconKey", id),
    userDescription: requireNonEmpty(row.userDescription, "userDescription", id),
    imagePrompt: requireNonEmpty(row.imagePrompt, "imagePrompt", id),
    gridSize: {
      width,
      height,
    },
    gridMap: requireNonEmpty(row.gridMap, "gridMap", id),
    wiringLanes,
    projectileMuzzles,
    projectileAssetKey: requireNonEmpty(row.projectileAssetKey, "projectileAssetKey", id),
    shipScale: requirePositiveFiniteNumber(row.shipScale, "shipScale", id),
    enemyScale: requirePositiveFiniteNumber(row.enemyScale, "enemyScale", id),
    unlockCondition: toUnlockCondition(row, id),
    smallAssetKey: requireNonEmpty(row.smallAssetKey, "smallAssetKey", id),
    largeAssetKey: requireNonEmpty(row.largeAssetKey, "largeAssetKey", id),
  };
}

function buildSpaceshipDefinitions(rows: readonly SpaceshipRow[]): readonly SpaceshipDefinition[] {
  const definitions = rows.map(toSpaceshipDefinition);
  if (definitions.length === 0) throw new Error("spaceships.tsv: at least one spaceship is required");
  const seen = new Set<string>();
  const duplicates: string[] = [];
  for (const def of definitions) {
    if (seen.has(def.id)) duplicates.push(def.id);
    seen.add(def.id);
  }
  if (duplicates.length > 0) throw new Error(`spaceships.tsv: duplicate id ${duplicates.join(", ")}`);
  return definitions;
}

export const SPACESHIP_DEFS = buildSpaceshipDefinitions(spaceshipsJson as readonly SpaceshipRow[]);
export const DEFAULT_SPACESHIP_ID = globalRepo.getString(K.defaultSpaceshipId);

export function spaceshipById(id: string): SpaceshipDefinition | undefined {
  return SPACESHIP_DEFS.find((spaceship) => spaceship.id === id);
}

export function defaultSpaceship(): SpaceshipDefinition {
  const def = spaceshipById(DEFAULT_SPACESHIP_ID);
  if (!def) throw new Error(`global.tsv: defaultSpaceshipId '${DEFAULT_SPACESHIP_ID}' is not defined in spaceships.tsv`);
  return def;
}

export function initialUnlockedCellsForSpaceship(def: SpaceshipDefinition): GridPosition[] {
  const unlockedCells: GridPosition[] = [];
  const rows = def.gridMap.split("/");
  for (let y = 0; y < rows.length; y++) {
    const row = rows[y]!;
    for (let x = 0; x < row.length; x++) {
      const ch = row[x]!;
      if (ch === "L") unlockedCells.push({ x, y });
    }
  }
  return unlockedCells;
}

export function blockedCellsForSpaceship(def: SpaceshipDefinition): GridPosition[] {
  const blockedCells: GridPosition[] = [];
  const rows = def.gridMap.split("/");
  for (let y = 0; y < rows.length; y++) {
    const row = rows[y]!;
    for (let x = 0; x < row.length; x++) {
      if (row[x] === "#") blockedCells.push({ x, y });
    }
  }
  return blockedCells;
}

function validModuleCells(def: SpaceshipDefinition): Set<string> {
  const keys = new Set<string>();
  const rows = def.gridMap.split("/");
  for (let y = 0; y < rows.length; y++) {
    const row = rows[y]!;
    for (let x = 0; x < row.length; x++) {
      if (row[x] !== "#") keys.add(`${x},${y}`);
    }
  }
  return keys;
}

export function sanitizeSpaceshipUnlockedCells(
  def: SpaceshipDefinition,
  cells: readonly GridPosition[] | undefined,
): GridPosition[] {
  const valid = validModuleCells(def);
  const initial = initialUnlockedCellsForSpaceship(def);
  const out: GridPosition[] = [];
  const seen = new Set<string>();
  const push = (cell: GridPosition): void => {
    const key = `${cell.x},${cell.y}`;
    if (!valid.has(key) || seen.has(key)) return;
    seen.add(key);
    out.push({ x: cell.x, y: cell.y });
  };
  initial.forEach(push);
  cells?.forEach(push);
  return out;
}

export function moduleGridStateForSpaceship(
  def: SpaceshipDefinition,
  unlockedCells?: readonly GridPosition[],
): ModuleGridState {
  const initialUnlockedCells = initialUnlockedCellsForSpaceship(def);
  const sanitizedUnlockedCells = sanitizeSpaceshipUnlockedCells(def, unlockedCells ?? initialUnlockedCells);
  const primary = def.wiringLanes[0]!;
  return {
    width: def.gridSize.width,
    height: def.gridSize.height,
    start: { ...primary.start },
    fire: { ...primary.fire },
    lanes: def.wiringLanes.map((lane) => ({
      start: { ...lane.start },
      fire: { ...lane.fire },
    })),
    blockedCells: blockedCellsForSpaceship(def),
    baseUnlockedCount: initialUnlockedCells.length,
    unlockedCells: sanitizedUnlockedCells,
    placedModules: [],
  };
}
